
    import MaterialTreeList from '../material-tree-list.vue'
    import { useSparePartsStore } from '@/store/spareParts'
    import { useEquipmentStore } from '@/store/equipment'
    import { NotificationDTO, useNotificationStore } from '@/store/notification'

    import { defineComponent, computed, watch, ref, reactive } from 'vue'
    import { buildBomItemLocalization, buildLocalization } from '@/util'
    import { RadioButtonProps, RadioGroup, Checkbox, Input } from '@progress/kendo-vue-inputs'
    import { Button } from '@progress/kendo-vue-buttons'
    import { Label } from '@progress/kendo-vue-labels'
    import SearchInput from '@/components/search-input.vue'
    import Loader from '@/components/common/loader.vue'

    import { DbConstants } from '@/const'
    import { AxiosError, HttpStatusCode } from 'axios'
    import { IBomHeaderDTO } from '@/types/bom'
    import { ISearchInput } from '@/types/search'

    interface IFilterData {
        withProductionBom: boolean,
        withMaintenanceBom: boolean,
        withRecommendedItemsBom: boolean,
        withAlternativeItemsBom: boolean,
        withReplacement: boolean,
        withPicture: boolean,
        withNoPicture: boolean,
        withPdfFile: boolean,
        withNoPdfFile: boolean
    }

    interface IDataSource {
        filterMode: 'all' | 'equipment',
        filter: IFilterData,
        equipmentSn: string | null
    }

    export default defineComponent({
        name: 'AdminSparePartOverview',

        components: {
            MaterialTreeList,
            SearchInput,
            RadioGroup,
            'k-button': Button,
            'k-label': Label,
            'k-input': Input,
            Checkbox,
            Loader
        },

        setup() {
            /********************************************************************************************************************
             PRIVATE DATA
             ********************************************************************************************************************/
            const sparePartStore = useSparePartsStore()
            const equipmentStore = useEquipmentStore()
            const notificationStore = useNotificationStore()

            /********************************************************************************************************************
             PUBLIC DATA
             ********************************************************************************************************************/
            const dataSource = reactive<IDataSource>({
                filterMode: 'all',
                filter: {
                    withProductionBom: false,
                    withMaintenanceBom: false,
                    withRecommendedItemsBom: false,
                    withAlternativeItemsBom: false,
                    withReplacement: false,
                    withPicture: false,
                    withNoPicture: false,
                    withPdfFile: false,
                    withNoPdfFile: false
                },
                equipmentSn: null
            })

            const activeDataSource = ref<IDataSource>(cloneDataSource(dataSource))

            const errorMessage = ref<string | null>(null)
            const eqRef = ref<InstanceType<typeof Input> | null>(null)

            const searchText = ref<string | null>('hans')
            const activeSearchInput = ref<ISearchInput>({
                searchText: '',
                jumpToFirstResult: false
            })
            const searchInputRef = ref<InstanceType<typeof SearchInput> | null>(null);

            const data = { dataSource, searchText, activeSearchInput, errorMessage, eqRef, searchInputRef }

            /********************************************************************************************************************
             COMPUTED PROPERTIES
             ********************************************************************************************************************/
            const allSpareParts = computed(() => sparePartStore.spareParts)
            const equipmentSpareParts = computed(() => equipmentStore.bomData)
            const isEquipmentMode = computed(() => activeDataSource.value.filterMode != 'all')
            const spareParts = computed(() => isEquipmentMode.value
                ? equipmentSpareParts.value
                : allSpareParts.value
            )

            const filteredSpareParts = computed(() => applyFilter(activeDataSource.value.filter, spareParts.value ?? []))

            const hasLoaded = computed(() => spareParts.value != null)
            const anyData = computed(() => Boolean(spareParts.value?.length))
            const anyFilteredData = computed(() => Boolean(filteredSpareParts.value?.length))

            const choices = computed(() => [
                {
                    label: localize('AllSpareParts'),
                    value: 'all'
                },
                {
                    label: localize('EquipmentSN'),
                    value: 'equipment'
                }
            ] as RadioButtonProps[])

            const filterAllowed = computed(() => dataSource.filterMode == 'all' || dataSource.equipmentSn != null)
            const computedData = {
                isEquipmentMode,
                spareParts,
                filteredSpareParts,
                hasLoaded,
                anyData,
                anyFilteredData,
                choices,
                filterAllowed
            }

            /********************************************************************************************************************
             WATCHERS
             ********************************************************************************************************************/
            watch(dataSource, (newValue) => {
                if (newValue.filterMode === 'equipment') {
                    setTimeout(() => focusEquipmentInput(), 10)
                }
            })

            /********************************************************************************************************************
             PRIVATE METHODS
             ********************************************************************************************************************/
            function applyFilter(filter: IFilterData, data: IBomHeaderDTO[]): IBomHeaderDTO[] {
                let result = data

                // First apply BOM filter
                if (filter.withProductionBom || filter.withMaintenanceBom || filter.withRecommendedItemsBom || filter.withAlternativeItemsBom || filter.withReplacement) {
                    result = result.filter(bomItem =>
                        (filter.withProductionBom && bomItem.bomInstances.findIndex(bom => bom.bomUsage == DbConstants.materialBomUsage.Default) >= 0) ||
                        (filter.withMaintenanceBom && bomItem.bomInstances.findIndex(bom => bom.bomUsage == DbConstants.materialBomUsage.Maintenance) >= 0) ||
                        (filter.withRecommendedItemsBom && bomItem.bomInstances.findIndex(bom => bom.bomUsage == DbConstants.materialBomUsage.RecommendedItems) >= 0) ||
                        (filter.withAlternativeItemsBom && bomItem.bomInstances.findIndex(bom => bom.bomUsage == DbConstants.materialBomUsage.AlternativeItems) >= 0) ||
                        (filter.withReplacement && (bomItem.replacement != null || bomItem.bomInstances.findIndex(bom => bom.bomUsage == DbConstants.materialBomUsage.Replacement)) >= 0))
                }

                // Then apply material filter
                if (filter.withPicture) {
                    result = result.filter(bomItem => bomItem.material.documents.findIndex(doc => doc.documentType == "RES") >= 0)
                }

                if (filter.withNoPicture) {
                    result = result.filter(bomItem => bomItem.material.documents.findIndex(doc => doc.documentType == "RES") == -1)
                }

                if (filter.withPdfFile) {
                    result = result.filter(bomItem => bomItem.material.documents.findIndex(doc => doc.documentType != "RES") >= 0)
                }

                if (filter.withNoPdfFile) {
                    result = result.filter(bomItem => bomItem.material.documents.findIndex(doc => doc.documentType != "RES") == -1)
                }

                return result
            }

            function handleError(error: AxiosError | Error) {
                if (error instanceof AxiosError && error.response?.status == HttpStatusCode.NotFound) {
                    notificationStore.addNotification(new NotificationDTO(localize('unknownDevice'), 'warning'))
                    errorMessage.value = localize('notFound')
                }
                else {
                    notificationStore.addNotification(new NotificationDTO(error.message, 'error'))
                    errorMessage.value = localize('unexpectedError')
                }
            }

            /********************************************************************************************************************
             PUBLIC METHODS AND EVENT HANDLERS
             ********************************************************************************************************************/
            const { localize } = buildLocalization()
            const { localizeBomItemText } = buildBomItemLocalization()

            function focusEquipmentInput() {
                if (eqRef.value != null) {
                    eqRef.value.$el.focus()
                }
            }

            function onApplyFilter() {
                // reset search
                searchInputRef.value?.clearSearch() 
                errorMessage.value = null

                const loadNewEquipmentData = dataSource.filterMode === 'equipment' &&
                    dataSource.equipmentSn != null &&
                    dataSource.equipmentSn != activeDataSource.value.equipmentSn

                activeDataSource.value = cloneDataSource(dataSource)

                if (loadNewEquipmentData) {
                    equipmentStore.setDevice(dataSource.equipmentSn!)
                        .catch((error: AxiosError | Error) => {                            
                            activeDataSource.value.equipmentSn = null
                            handleError(error)                            
                        })
                }
            }

            function cloneDataSource(source: IDataSource): IDataSource {
                return {
                    filterMode: source.filterMode,
                    filter: { ...source.filter },
                    equipmentSn: source.equipmentSn
                }
            }

            function checkFilterSubmit(e: KeyboardEvent) {
                if (e.key == 'Enter') {
                    onApplyFilter()
                }
            }

            function setFilter(elementId: string) {
                switch (elementId) {
                    case "chkFilterHasPicture":
                        if (!dataSource.filter.withPicture) dataSource.filter.withNoPicture = false
                        break

                    case "chkFilterHasNoPicture":
                        if (!dataSource.filter.withNoPicture) dataSource.filter.withPicture = false
                        break

                    case "chkFilterHasPdfFiles":
                        if (!dataSource.filter.withPdfFile) dataSource.filter.withNoPdfFile = false
                        break

                    case "chkFilterHasNoPdfFiles":
                        if (!dataSource.filter.withNoPdfFile) dataSource.filter.withPdfFile = false
                        break

                    default:
                        break
                }
            }

            const functions = { localize, localizeBomItemText, focusEquipmentInput, onApplyFilter, setFilter, checkFilterSubmit }

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return {
                // DATA
                ...data,
                // COMPUTED
                ...computedData,
                // METHODS AND EVENT HANDLERS
                ...functions
            }
        }
    })

